import { useState } from 'react';
import { iconeDe, alignementDe, Puce } from './apparence';

const FILES_BASE_URL = import.meta.env.VITE_FILES_BASE_URL || 'https://www.cabaza.be/files';

/**
 * Des contenus alternatifs, un seul visible à la fois.
 *
 * ========================================================================
 * POURQUOI CE BLOC EXISTE
 * ========================================================================
 *
 * La page d'installation décrit TROIS procédures incompatibles — iPhone,
 * Android, ordinateur. Les empiler les unes sous les autres obligerait
 * chaque lecteur à traverser deux méthodes qui ne le concernent pas, et la
 * plus probable serait qu'il suive la mauvaise.
 *
 * ========================================================================
 * CE QU'IL NE FAUT PAS FAIRE : DEVINER L'APPAREIL
 * ========================================================================
 *
 * Il serait tentant de présélectionner l'onglet d'après le navigateur. On ne
 * le fait pas : quelqu'un consulte souvent la page depuis son ordinateur
 * pour installer sur son téléphone, et une détection qui se trompe cache la
 * bonne réponse derrière un onglet que l'utilisateur ne pensera pas à
 * ouvrir. Le premier onglet est actif, les autres sont visibles — chacun
 * choisit.
 *
 * Structure attendue :
 *   { type: "onglets", titre?, onglets: [ { libelle, contenu?, etapes?[] } ] }
 *
 * `etapes` reprend la forme du bloc Etapes (titre, texte, astuce, image) :
 * même rendu, même vocabulaire, une seule chose à apprendre.
 */
const OngletsBloc = ({ bloc }) => {
    const onglets = Array.isArray(bloc?.onglets) ? bloc.onglets : [];
    const [actif, setActif] = useState(0);

    if (onglets.length === 0) return null;

    const courant = onglets[Math.min(actif, onglets.length - 1)] || {};
    const etapes = Array.isArray(courant.etapes) ? courant.etapes : [];

    return (
        <section className="mb-10" data-testid="bloc-onglets">
            {bloc.titre && (
                <h2 className="text-xl font-bold text-gray-900 mb-5">{bloc.titre}</h2>
            )}

            {/* role="tablist" : sans lui, un lecteur d'écran annonce une suite
                de boutons sans dire qu'ils s'excluent. */}
            <div
                role="tablist"
                className="inline-flex flex-wrap gap-1 p-1 bg-gray-100 rounded-xl mb-6"
            >
                {onglets.map((o, i) => (
                    <button
                        key={i}
                        role="tab"
                        aria-selected={i === actif}
                        onClick={() => setActif(i)}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                            i === actif
                                ? 'bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white shadow-sm'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-white/60'
                        }`}
                    >
                        {o.libelle || `Onglet ${i + 1}`}
                    </button>
                ))}
            </div>

            <div role="tabpanel" className="bg-white rounded-2xl border p-5 sm:p-6">
                {courant.titre && (
                    <h3 className="font-semibold text-gray-900 text-lg">{courant.titre}</h3>
                )}
                {courant.sousTitre && (
                    <p className="text-sm text-gray-600 mt-1">{courant.sousTitre}</p>
                )}

                {courant.contenu && (
                    <p className="text-sm text-gray-600 mt-2">{courant.contenu}</p>
                )}

                {etapes.length > 0 && (
                    <ol className="space-y-4 mt-5">
                        {etapes.map((e, i) => (
                            <li key={i} className="flex gap-4">
                                <Puce index={i} icone={e.icone} />
                                <div className="pt-0.5 min-w-0 flex-1">
                                    {e.titre && (
                                        <h4 className="font-semibold text-gray-900">{e.titre}</h4>
                                    )}
                                    {e.texte && (
                                        <p className="text-sm text-gray-600 mt-0.5">{e.texte}</p>
                                    )}
                                    {e.astuce && (
                                        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
                                            {e.astuce}
                                        </p>
                                    )}
                                    {e.image && (
                                        <img
                                            src={e.image.startsWith('http') || e.image.startsWith('/')
                                                ? e.image
                                                : `${FILES_BASE_URL}/${e.image}`}
                                            alt=""
                                            loading="lazy"
                                            className="mt-3 rounded-lg border max-w-full h-auto"
                                        />
                                    )}
                                </div>
                            </li>
                        ))}
                    </ol>
                )}
            </div>
        </section>
    );
};

export default OngletsBloc;
